import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CreateReconciliationInput } from '@accounting/shared';
import {
  useCorrectReconciliation,
  useCreateReconciliation,
  useDeleteInProgressReconciliation,
  useReconciliations,
  type ReconciliationWithStatus,
} from '../../hooks/useReconciliation';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { StatementDropStart } from '../../components/reconciliation/StatementDropStart';
import { LinkFiledStatementsPanel } from '../../components/reconciliation/LinkFiledStatementsPanel';
import { openStatement, useStatements } from '../../hooks/useStatements';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError, openFileInNewTab } from '../../lib/api';
import { formatAccountingDate, formatCurrency, humanizeLabel, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reconcile/')({
  component: ReconcilePage,
});

function statusTag(status: string) {
  switch (status) {
    case 'completed':
      return <span className="tag ok">completed</span>;
    case 'in_progress':
      return <span className="tag warn">in progress</span>;
    case 'voided':
      return <span className="tag bad">voided</span>;
    default:
      return <span className="tag">{status}</span>;
  }
}

/**
 * Accounts offered on the reconcile screen.
 *
 * Every asset and liability is technically reconcilable, and listing all of
 * them made it easy to start a reconciliation against the wrong account. So
 * the list is now opt-in: only accounts flagged "Reconcilable" on the Chart of
 * Accounts appear. Linking a bank account flags its GL account automatically,
 * so the common path needs no setup.
 */
function eligibleAccounts<
  T extends {
    isActive: boolean;
    allowDirectPosting: boolean;
    accountType: string;
    isReconcilable: boolean;
  },
>(accounts: T[]): T[] {
  return accounts.filter(
    (a) =>
      a.isActive &&
      a.allowDirectPosting &&
      a.isReconcilable &&
      (a.accountType === 'asset' || a.accountType === 'liability'),
  );
}

function ReconcilePage() {
  const companyId = useActiveCompanyId();
  const { data: reconciliations = [], isLoading, error } = useReconciliations(companyId);
  const { data: accounts = [] } = useAccounts(companyId);
  // All statements for the company — so we can map reconciliationId → linked statement
  // for the per-row download icon. Cheap: returns a few KB even with many statements.
  const { data: allStatements = [] } = useStatements(companyId, {}, { enabled: true });
  const statementByRecon = new Map(
    allStatements
      .filter((s) => s.reconciliationId)
      .map((s) => [s.reconciliationId as string, s] as const),
  );
  // Two ways in: drop the statement and let it fill the form, or key it in.
  // The statement route is offered first because it is the one that also brings
  // the matching with it.
  const [starting, setStarting] = useState<'none' | 'statement' | 'manual'>('none');
  // Pair statements already in Documents with completed reconciliations.
  const [linking, setLinking] = useState(false);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Reconciliation</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  const eligible = eligibleAccounts(accounts);

  const [accountFilter, setAccountFilter] = useState<string>('');
  const [hideVoided, setHideVoided] = useState(true);
  const [correctTarget, setCorrectTarget] = useState<ReconciliationWithStatus | null>(null);
  const discard = useDeleteInProgressReconciliation(companyId);

  // Show void rows folded into their originals: hide standalone voided rows that
  // point back at a completed row (the completed row is already marked voided).
  const unfolded = reconciliations.filter(
    (r) => !(r.status === 'voided' && r.priorReconciliationId),
  );
  const inProgress = reconciliations.filter((r) => r.effectiveStatus === 'in_progress');
  const visible = unfolded
    .filter((r) => !accountFilter || r.glAccountId === accountFilter)
    .filter((r) => !hideVoided || r.effectiveStatus !== 'voided');

  // Reconciling is done one account at a time, month after month, so the list
  // reads best as a block per account with the newest statement first — rather
  // than every account's history interleaved by whatever order the API returned.
  const grouped = useMemo(() => {
    const byAccount = new Map<
      string,
      { code: string; name: string; rows: ReconciliationWithStatus[] }
    >();
    for (const r of visible) {
      const key = r.glAccountId ?? 'unknown';
      if (!byAccount.has(key)) {
        byAccount.set(key, {
          code: r.glAccountCode ?? '',
          name: r.glAccountName ?? 'Account',
          rows: [],
        });
      }
      byAccount.get(key)!.rows.push(r);
    }
    for (const g of byAccount.values()) {
      // Newest statement at the top of each account — the one being worked on.
      g.rows.sort((a, b) => b.statementDate.localeCompare(a.statementDate));
    }
    return [...byAccount.entries()]
      .map(([id, g]) => ({ id, ...g }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [visible]);

  // Accounts that actually appear in the recon list — used to populate the
  // filter dropdown so the user only sees accounts they've reconciled before.
  const accountsInList = useMemo(() => {
    const seen = new Map<string, { id: string; code: string; name: string }>();
    for (const r of reconciliations) {
      if (r.glAccountId && r.glAccountCode && r.glAccountName && !seen.has(r.glAccountId)) {
        seen.set(r.glAccountId, { id: r.glAccountId, code: r.glAccountCode, name: r.glAccountName });
      }
    }
    return [...seen.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [reconciliations]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Reconciliation</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Pick a reconcilable account, tick the journal entries that appear on your statement, then
            lock the period.
          </p>
        </div>
        {starting === 'none' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn-secondary"
              onClick={() => setLinking((v) => !v)}
              title="Pair statements already uploaded through Documents with completed reconciliations that have none"
            >
              🔗 Link filed statements
            </button>
            <button
              className="btn-secondary"
              onClick={() => setStarting('manual')}
              disabled={eligible.length === 0}
              title={
                eligible.length === 0
                  ? 'Mark an account "Reconcilable" on the Chart of Accounts first'
                  : 'Key in the statement date and balances yourself'
              }
            >
              Enter by hand
            </button>
            <button
              className="btn-primary"
              onClick={() => setStarting('statement')}
              disabled={eligible.length === 0}
              title={
                eligible.length === 0
                  ? 'Mark an account "Reconcilable" on the Chart of Accounts first'
                  : 'Drop the statement PDF and let it fill in the dates, balances and matches'
              }
            >
              📄 Start from statement
            </button>
          </div>
        )}
      </div>

      {/* An in-progress reconciliation was only reachable by spotting its
        * "Continue" button somewhere down a list of completed ones — and
        * starting a new one just returned "complete or discard it first" with
        * no way to reach the thing. Surface it, with both exits. */}
      {inProgress.length > 0 && starting === 'none' && (
        <div className="card accent-bank" style={{ marginBottom: 16, padding: 14 }}>
          <strong style={{ fontSize: 14 }}>
            {inProgress.length === 1
              ? 'You have a reconciliation in progress'
              : `You have ${inProgress.length} reconciliations in progress`}
          </strong>
          <p className="muted" style={{ margin: '4px 0 10px', fontSize: 13 }}>
            Your ticks are saved — picking one up carries on where you left off. An account can only
            have one at a time, so discard it if you want to start that account over.
          </p>
          {inProgress.map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                padding: '6px 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ flex: 1, minWidth: 220, fontSize: 13 }}>
                <strong>
                  {r.glAccountCode ? r.glAccountCode + ' ' : ''}
                  {r.glAccountName ?? 'Account'}
                </strong>
                <span className="muted"> · statement {formatAccountingDate(r.statementDate)}</span>
              </span>
              <Link
                to="/reconcile/$id"
                params={{ id: r.id }}
                className="btn-primary"
                style={{ textDecoration: 'none' }}
              >
                Pick up where I left off
              </Link>
              <button
                type="button"
                className="btn-danger"
                disabled={discard.isPending}
                title="Delete this in-progress reconciliation. Nothing is posted or locked by it, so nothing in the ledger changes."
                onClick={() => {
                  if (
                    confirm(
                      `Discard the in-progress reconciliation for ${r.glAccountName ?? 'this account'} (statement ${r.statementDate})?\n\nYour ticks are thrown away. Nothing in the ledger changes — an in-progress reconciliation posts and locks nothing.`,
                    )
                  ) {
                    discard.mutate(r.id);
                  }
                }}
              >
                Discard
              </button>
            </div>
          ))}
        </div>
      )}

      {eligible.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 24 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No accounts are marked <strong>Reconcilable</strong> yet. Mark the bank, credit-card and
            loan accounts you receive statements for — only those appear here, so you can&rsquo;t
            start a reconciliation against the wrong account by mistake.
          </p>
          <Link to="/accounts" className="btn-secondary" style={{ textDecoration: 'none' }}>
            Go to Chart of Accounts
          </Link>
        </div>
      )}

      {linking && starting === 'none' && (
        <LinkFiledStatementsPanel companyId={companyId} onClose={() => setLinking(false)} />
      )}

      {starting === 'statement' && (
        <StatementDropStart
          companyId={companyId}
          accounts={eligible}
          onCancel={() => setStarting('none')}
        />
      )}

      {starting === 'manual' && (
        <CreateReconciliationForm
          companyId={companyId}
          accounts={eligible}
          onClose={() => setStarting('none')}
        />
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load reconciliations.</p>}

      {!isLoading && unfolded.length === 0 && starting === 'none' && eligible.length > 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No reconciliations yet.</p>
        </div>
      )}

      {unfolded.length > 0 ? (
        <>
          {(accountsInList.length > 1 || unfolded.some((r) => r.effectiveStatus === 'voided')) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
              {accountsInList.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label htmlFor="account-filter" className="muted" style={{ fontSize: 13 }}>
                    Account:
                  </label>
                  <select
                    id="account-filter"
                    value={accountFilter}
                    onChange={(e) => setAccountFilter(e.target.value)}
                    style={{ minWidth: 240 }}
                  >
                    <option value="">All accounts</option>
                    {accountsInList.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} · {a.name}
                      </option>
                    ))}
                  </select>
                  {accountFilter && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setAccountFilter('')}
                      style={{ fontSize: 12, padding: '4px 10px' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
              {unfolded.some((r) => r.effectiveStatus === 'voided') && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={hideVoided}
                    onChange={(e) => setHideVoided(e.target.checked)}
                  />
                  Hide voided
                </label>
              )}
            </div>
          )}
          {visible.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 24 }}>
              <p className="muted" style={{ marginTop: 0 }}>No reconciliations match the current filters.</p>
            </div>
          ) : (
        <table className="table">
          <thead>
            <tr>
              {/* No Account column: each block is headed by its account, so
                * repeating it on every row is noise. */}
              <th>Statement date</th>
              <th style={{ textAlign: 'right' }}>Starting balance</th>
              <th style={{ textAlign: 'right' }}>Ending balance</th>
              <th style={{ textAlign: 'right' }}>Difference</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {grouped.flatMap((g) => [
              <tr key={`hdr-${g.id}`}>
                <th
                  colSpan={6}
                  style={{
                    textAlign: 'left',
                    background: 'var(--bg-surface)',
                    fontSize: 13,
                    paddingTop: 10,
                  }}
                >
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{g.code}</span> · {g.name}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {' '}
                    · {g.rows.length} statement{g.rows.length === 1 ? '' : 's'}
                  </span>
                </th>
              </tr>,
              ...g.rows.map((r: ReconciliationWithStatus) => (
              <tr key={r.id}>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {formatAccountingDate(r.statementDate)}
                    {statementByRecon.get(r.id) && (
                      <button
                        type="button"
                        title={`Download statement: ${statementByRecon.get(r.id)!.fileName}`}
                        aria-label="Download statement"
                        onClick={() => void openStatement(companyId, statementByRecon.get(r.id)!.id)}
                        style={{
                          background: 'none', border: 0, padding: 2, cursor: 'pointer',
                          color: 'var(--text-muted)', lineHeight: 1, fontSize: 14,
                        }}
                      >
                        📄
                      </button>
                    )}
                    {r.effectiveStatus === 'completed' && (
                      <button
                        type="button"
                        title="Download reconciliation report (PDF)"
                        aria-label="Download reconciliation report"
                        onClick={() =>
                          void openFileInNewTab(`/companies/${companyId}/reconciliations/${r.id}/report.pdf`)
                        }
                        style={{
                          background: 'none', border: 0, padding: 2, cursor: 'pointer',
                          color: 'var(--text-muted)', lineHeight: 1, fontSize: 14,
                        }}
                      >
                        📋
                      </button>
                    )}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.statementBeginningBalance)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.statementEndingBalance)}</td>
                <td style={{ textAlign: 'right' }}>
                  {r.difference !== null ? formatCurrency(r.difference, true) : '—'}
                </td>
                <td>{statusTag(r.effectiveStatus)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {r.effectiveStatus === 'completed' && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setCorrectTarget(r)}
                        title="Void this reconciliation and start a new one with the typo fixed (hash chain stays intact)"
                        style={{ fontSize: 12, padding: '4px 10px' }}
                      >
                        Correct…
                      </button>
                    )}
                    <Link
                      to="/reconcile/$id"
                      params={{ id: r.id }}
                      className="btn-secondary"
                      style={{ textDecoration: 'none' }}
                    >
                      {r.effectiveStatus === 'in_progress' ? 'Continue' : 'View'}
                    </Link>
                  </div>
                </td>
              </tr>
              )),
            ])}
          </tbody>
        </table>
          )}
        </>
      ) : null}

      {correctTarget && (
        <CorrectReconciliationModal
          companyId={companyId}
          target={correctTarget}
          onClose={() => setCorrectTarget(null)}
        />
      )}
    </div>
  );
}

function CorrectReconciliationModal({
  companyId,
  target,
  onClose,
}: {
  companyId: string;
  target: ReconciliationWithStatus;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const correct = useCorrectReconciliation(companyId);

  // 2dp display for the money fields so the user sees "1234.56", not "1234.5600".
  const fmtMoney = (s: string) => {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n.toFixed(2) : s;
  };

  const [form, setForm] = useState({
    statementDate: target.statementDate,
    statementBeginningBalance: fmtMoney(target.statementBeginningBalance),
    statementEndingBalance: fmtMoney(target.statementEndingBalance),
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);

  const cleanMoney = (s: string) => s.replace(/[$,\s]/g, '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.reason.trim()) {
      setError('Please describe why you are correcting this reconciliation.');
      return;
    }
    // Only send fields that actually changed; the server keeps the originals
    // for the rest. Money values normalised before comparison.
    const payload: import('@accounting/shared').CorrectReconciliationInput = {
      reason: form.reason.trim(),
    };
    if (form.statementDate !== target.statementDate) {
      payload.statementDate = form.statementDate;
    }
    const newBegin = cleanMoney(form.statementBeginningBalance);
    if (parseFloat(newBegin) !== parseFloat(target.statementBeginningBalance)) {
      payload.statementBeginningBalance = newBegin;
    }
    const newEnd = cleanMoney(form.statementEndingBalance);
    if (parseFloat(newEnd) !== parseFloat(target.statementEndingBalance)) {
      payload.statementEndingBalance = newEnd;
    }
    try {
      const result = await correct.mutateAsync({ id: target.id, input: payload });
      onClose();
      // Land on the new in-progress recon so the user can review + Complete.
      void navigate({
        to: '/reconcile/$id',
        params: { id: result.newReconciliation.id },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to correct reconciliation.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to correct reconciliation.');
      }
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 460, maxWidth: '92vw', padding: 20 }}
      >
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Correct reconciliation</h2>
        <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>
          The original will be voided and a new in-progress reconciliation will open with the
          same items pre-checked. The hash chain records both events.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label className="form-label">Statement date</label>
            <input
              type="date"
              value={form.statementDate}
              onChange={(e) => setForm((p) => ({ ...p, statementDate: e.target.value }))}
              required
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="form-label">Starting balance</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.statementBeginningBalance}
                onChange={(e) =>
                  setForm((p) => ({ ...p, statementBeginningBalance: e.target.value }))
                }
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label className="form-label">Ending balance</label>
              <input
                type="text"
                inputMode="decimal"
                value={form.statementEndingBalance}
                onChange={(e) =>
                  setForm((p) => ({ ...p, statementEndingBalance: e.target.value }))
                }
                style={{ width: '100%' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Reason (required for the audit trail)</label>
            <input
              type="text"
              value={form.reason}
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
              placeholder="Typo on statement date — was 2025, should be 2026"
              required
              style={{ width: '100%' }}
            />
          </div>

          {error && (
            <p className="field-error" style={{ marginBottom: 12 }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={correct.isPending}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={correct.isPending}>
              {correct.isPending ? 'Working…' : 'Void original and open corrected'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * "Next logical month-end" — bank statements close at month-end, so if the
 * prior reconciled statement was 2026-11-30 the next is 2026-12-31; if it was
 * 2026-12-31 the next is 2027-01-31. Day-of-month of the prior statement is
 * irrelevant; we always return the LAST day of the month AFTER the prior.
 *
 * Soft-populated only — the user can edit if their statement closes on a
 * different schedule.
 */
function nextMonthEndIso(priorIso: string): string {
  const parts = priorIso.split('-').map(Number);
  const year = parts[0]!;
  const month = parts[1]!; // 1-indexed
  // Date.UTC(year, month+1, 0) → day 0 of the month AFTER next-month → last day of next-month
  const dt = new Date(Date.UTC(year, month + 1, 0));
  return dt.toISOString().slice(0, 10);
}

function CreateReconciliationForm({
  companyId,
  accounts,
  onClose,
}: {
  companyId: string;
  accounts: import('@accounting/shared').Account[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const create = useCreateReconciliation(companyId);
  const { data: priorRecons = [] } = useReconciliations(companyId);

  const [form, setForm] = useState({
    glAccountId: '',
    statementDate: todayIso(),
    statementBeginningBalance: '0',
    statementEndingBalance: '',
  });

  useEffect(() => {
    if (!form.glAccountId && accounts.length > 0) {
      setForm((p) => ({ ...p, glAccountId: accounts[0]!.id }));
    }
  }, [accounts, form.glAccountId]);

  // Most recent STILL-STANDING completed reconciliation for the selected GL
  // account. Used to seed beginning balance + statement date so the user doesn't
  // have to type them (and so they can't accidentally close a session with
  // today's date as the statement date — which is immutable after completion).
  //
  // ⚠️ `effectiveStatus` only. A voided reconciliation still reads
  // status = 'completed' on its own row — voiding INSERTS a separate record
  // (Rule 6) rather than updating the original — so testing `status` as well
  // would seed from a month that has been unwound. After voiding July you are
  // redoing July, and the beginning balance to offer is June's ending balance,
  // not the one the voided July claimed.
  const priorForAccount = useMemo(() => {
    if (!form.glAccountId) return null;
    return priorRecons
      .filter((r) => r.glAccountId === form.glAccountId && r.effectiveStatus === 'completed')
      .sort((a, b) => b.statementDate.localeCompare(a.statementDate))[0] ?? null;
  }, [priorRecons, form.glAccountId]);

  // When the prior changes (because the user picked a different GL account, or
  // the list loaded), overwrite the soft-populated fields. Tracked by the
  // prior's ID so flipping accounts back-and-forth re-runs cleanly.
  const lastSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!priorForAccount) {
      // No prior recon → fall back to defaults (today + $0 beginning).
      lastSeededRef.current = null;
      return;
    }
    if (lastSeededRef.current === priorForAccount.id) return;
    lastSeededRef.current = priorForAccount.id;
    // The DB stores NUMERIC(19,4) so the API string comes back like "1234.5600".
    // Display as 2dp in the input — that's how the user thinks about a
    // statement balance, and the spec only uses 4dp internally for arithmetic.
    const beginning = parseFloat(priorForAccount.statementEndingBalance).toFixed(2);
    setForm((p) => ({
      ...p,
      statementBeginningBalance: beginning,
      statementDate: nextMonthEndIso(priorForAccount.statementDate),
    }));
  }, [priorForAccount]);

  const errorMessage =
    create.error instanceof ApiError
      ? (create.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : create.error?.message;

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        // Strip $ and thousands commas so a value pasted from a bank statement
        // (e.g. "$1,234.56") passes the API's strict money-string regex.
        const cleanMoney = (s: string) => s.replace(/[$,\s]/g, '');
        const input: CreateReconciliationInput = {
          glAccountId: form.glAccountId,
          statementDate: form.statementDate,
          statementBeginningBalance: cleanMoney(form.statementBeginningBalance) || '0',
          statementEndingBalance: cleanMoney(form.statementEndingBalance),
        };
        create.mutate(input, {
          onSuccess: (recon) => {
            onClose();
            void navigate({ to: '/reconcile/$id', params: { id: recon.id } });
          },
        });
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Start a reconciliation</h2>
      <div className="form-grid">
        <label htmlFor="ga">Account *</label>
        <AccountPicker
          id="ga"
          accounts={accounts}
          value={form.glAccountId}
          onChange={(id) => setForm((p) => ({ ...p, glAccountId: id }))}
          required
        />

        <label htmlFor="sd">Statement date *</label>
        <div>
          <input
            id="sd"
            type="date"
            value={form.statementDate}
            onChange={(e) => setForm((p) => ({ ...p, statementDate: e.target.value }))}
            required
          />
          {priorForAccount && (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              Auto-populated as next month-end after {formatAccountingDate(priorForAccount.statementDate)}. Change if needed.
            </div>
          )}
        </div>

        <label htmlFor="beg">Beginning balance</label>
        <div>
          <input
            id="beg"
            type="text"
            inputMode="decimal"
            value={form.statementBeginningBalance}
            onChange={(e) => setForm((p) => ({ ...p, statementBeginningBalance: e.target.value }))}
            placeholder="0.00"
            style={{ textAlign: 'right', maxWidth: 160 }}
          />
          {priorForAccount && (
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
              Auto-populated from prior statement's ending balance ({formatAccountingDate(priorForAccount.statementDate)}).
            </div>
          )}
        </div>

        <label htmlFor="end">Ending balance *</label>
        <input
          id="end"
          type="text"
          inputMode="decimal"
          value={form.statementEndingBalance}
          onChange={(e) => setForm((p) => ({ ...p, statementEndingBalance: e.target.value }))}
          placeholder="0.00"
          style={{ textAlign: 'right', maxWidth: 160 }}
          required
        />
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={create.isPending || !form.glAccountId || !form.statementEndingBalance.trim()}
        >
          {create.isPending ? 'Starting…' : 'Start'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
